import { ScriptureItem } from "./ScriptureItem";

//===== BASIC TYPES: Dates, Language, Country

// fuller implementation at https://spin.atomicobject.com/2017/06/19/strongly-typed-date-string-typescript/
export type DateString = string;

export interface Country {
  id: string; // "US"
  name: string; // "United States"
  nameLocal: string; // United States
}

export type ScriptDirection = "LTR" | "RTL";

export interface Language {
  id: string; // "eng"
  name: string; // "English"
  nameLocal: string; // "English"
  script: string; // "Latin"
  scriptDirection?: ScriptDirection; // ScriptDirection.LTR
}

//===== CHAPTER
interface ChapterRef {
  id: string;
  number: number;
  bookId: string;
}
export interface Chapter {
  id: string;
  bibleId: string;
  bookId: string;
  number: string;
  position: number;
  reference: string;
  copyright?: string;
  content?: ScriptureItem[];
  fumsToken: string;
  next?: ChapterRef;
  previous?: ChapterRef;
  parallels?: Omit<Omit<Chapter, "next">, "previous">[];
}

export function isChapter(c: any): c is Chapter {
  return !!(c && c.id && c.bookId && c.number);
}

export function isChapterWithText(c: any): c is Chapter {
  return Boolean(isChapter(c) && c.content);
}

//===== BOOK

export type BookId = string;

export interface Book {
  id: string; // "EXO"
  bibleId: string; // "555fef9a6cb31151-01"
  abbreviation: string; // "Ex"
  name: string; // "Exodus"
  nameLong: string; // "Exodus"
  chapters: Chapter[];
}

export function isBook(b: any): b is Book {
  return !!(b && b.id && b.bibleId && b.abbreviation);
}

export function isBookWithChapters(b: any): b is Book {
  return !!(
    isBook(b) &&
    b.chapters &&
    b.chapters.length &&
    isChapter(b.chapters[0]) &&
    isChapter(b.chapters[b.chapters.length - 1])
  );
}

export function isBookWithText(b: any): b is Book {
  return !!(
    isBook(b) &&
    b.chapters &&
    b.chapters.length &&
    isChapterWithText(b.chapters[0]) &&
    isChapterWithText(b.chapters[b.chapters.length - 1])
  );
}

//===== BIBLES
export interface BibleBase {
  id: string; // "4f12f582478c11e7-01"
  dblId: string; // "4f12f582478c11e7"
  name: string; // "English - 1991 Contemporary English (NT)"
  nameLocal: string; // "English - 1991 Contemporary English (NT)"
  books?: Book[];
}

export type BibleType = 'text' | 'audio';

export const BibleIdRe = /[\da-fA-F]{16}-\d\d/;

export interface AudioBible extends BibleBase {
}

export function filterBiblesByChapterId(
  bibles: AudioBible[] | Bible[],
  chapterId: string | undefined): AudioBible[] | Bible[] {
  const bookId = chapterId?.substr(0,3);
  return bibles.filter(
    bible => bible.books?.some(
      book => book.id === bookId
      && book.chapters.some(chapter => chapter.id === chapterId)));
}

export function isBibleId(id: string): boolean {
  return !!id.match(BibleIdRe);
}

export interface BibleMeta {
  disableFootnotes?: boolean;
  disableOffline?: boolean;
}

export interface Bible extends BibleBase {

  abbreviation?: string;
  abbreviationLocal?: string;
  description?: string;
  descriptionLocal?: string;
  language?: Language;
  countries?: Country[]; // | Country ??

  relatedDbl?: string | null;
  type?: BibleType;
  updatedAt?: DateString;
  copyright?: string;
  info?: string;
  audioBibles?: AudioBible[];

  meta?: BibleMeta;
}

export function isBible(bible: any): bible is Bible {
  return !!(bible && bible.id && bible.name && bible.abbreviation);
}

export function isBibleWithMetadata(bible: any): bible is Bible {
  return !!(
    isBible(bible) &&
    bible.books &&
    bible.books.length &&
    isBookWithChapters(bible.books[0]) &&
    isBookWithChapters(bible.books[bible.books.length - 1])
  );
}

export function isBibleWithText(bible: any): bible is Bible {
  return !!(
    isBible(bible) &&
    bible.books &&
    bible.books.length &&
    isBookWithText(bible.books[0]) &&
    isBookWithText(bible.books[bible.books.length - 1])
  );
}

export interface FumsMeta {
  fumsToken: string;
}
